import { Dialog, type DialogPassThroughOptions } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ZIndexUtils } from 'primereact/utils';
import {
    createContext,
    useContext,
    useEffect,
    useRef,
    useState,
    type MutableRefObject,
    type ReactNode,
} from 'react';
import { ToastContent } from '../../Toast/ToastContent';
import { useDoubleEscape } from '../hooks/useDoubleEscape';

interface DialogContextValue {
    close: () => void;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export function useDialogClose(): () => void {
    const context = useContext(DialogContext);
    if (!context) {
        throw new Error(
            'useDialogClose must be used within DialogWithDoubleEscape',
        );
    }
    return context.close;
}

interface DialogWithDoubleEscapeProps {
    onClose: () => void;
    onBeforeClose?: () => void;
    doubleEscapeEnabled?: boolean;
    children: ReactNode;
    className?: string;
    header?: ReactNode;
    footer?: ReactNode;
    pt?: DialogPassThroughOptions;
    closeRef?: MutableRefObject<(() => void) | undefined>;
    [key: string]: unknown;
}

export function DialogWithDoubleEscape({
    onClose,
    onBeforeClose,
    doubleEscapeEnabled = true,
    children,
    className,
    header,
    footer,
    pt,
    closeRef,
    ...dialogProps
}: DialogWithDoubleEscapeProps): ReactNode {
    const [visible, setVisible] = useState(true);
    const toastRef = useRef<Toast>(null);
    const dialogRef = useRef<Dialog>(null);
    const maskElRef = useRef<HTMLDivElement | null>(null);
    maskElRef.current = dialogRef.current?.getMask() ?? maskElRef.current;

    const actualClose = (): void => {
        setVisible(false);
        onClose();
    };

    if (closeRef) {
        closeRef.current = actualClose;
    }

    useEffect(() => {
        return () => {
            const maskEl = maskElRef.current;
            if (maskEl) {
                ZIndexUtils.clear(maskEl);
                maskEl.style.pointerEvents = 'none';
                maskEl.style.display = 'none';
            }
        };
    }, []);

    const close = (): void => {
        if (onBeforeClose) {
            onBeforeClose();
        } else {
            actualClose();
        }
    };

    useDoubleEscape({
        onClose: close,
        onShowFeedback: (timeWindow) => {
            toastRef.current?.show({
                severity: 'info',
                summary: 'Info',
                detail: 'Press ESC again to close',
                content: ToastContent,
                life: timeWindow,
            });
        },
        enabled: doubleEscapeEnabled,
    });

    const handleHide = (): void => {
        if (!visible) {
            return;
        }
        close();
    };

    return (
        <DialogContext.Provider value={{ close }}>
            <Toast ref={toastRef} position="top-center" baseZIndex={100} />
            <Dialog
                {...dialogProps}
                ref={dialogRef}
                visible={visible}
                modal
                draggable={false}
                focusOnShow={false}
                closeOnEscape={false}
                className={className}
                header={header}
                footer={footer}
                closable={false}
                onHide={handleHide}
                pt={pt}
            >
                {children}
            </Dialog>
        </DialogContext.Provider>
    );
}
